import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import { formatExpiryAlert } from "../../../utils/clientAppUtils";
import {
  formatNotificationLabel,
  formatNotificationText,
  formatPollNotificationItem,
  mergeClientNotificationItems,
} from "../../../utils/notificationDisplay";
import {
  buildPollRespondPayload,
  normalizeSurveyRows,
  notifyPollAnswered,
  pollNeedsResponse,
} from "../../../utils/clientSessionUtils";
import s from "./clientApp.module.css";
import { Button, Skeleton, useToast } from "../../ui";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  return formatDateRu(iso);
}

function PollCard({ row, selected, onSelect, onSubmit, busy }) {
  const normalized = normalizeSurveyRows([row])[0] || row;
  const poll = normalized.poll || row.poll;
  const session = normalized.session || row.session;
  const options = poll?.options || row.options || [];
  const question =
    poll?.questions?.[0]?.text || row.question || poll?.title || row.title;

  return (
    <article className={s.notifPoll}>
      <span className={s.notifTag}>Опрос</span>
      <strong>{poll?.title}</strong>
      {session?.start_datetime ? (
        <p className={s.notifMeta}>
          {session.group?.name ? `${session.group.name} · ` : ""}
          {formatDateRu(session.start_datetime)}{" "}
          {formatTimeRu(session.start_datetime)}
        </p>
      ) : null}
      <p>{question}</p>
      <div className={s.notifOptions}>
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={selected === opt.id ? s.notifOptOn : s.notifOpt}
            disabled={busy}
            onClick={() => onSelect(opt.id)}
          >
            {opt.text}
          </button>
        ))}
      </div>
      <Button type="button" size="sm" disabled={busy || !selected} onClick={onSubmit}>
        {row.responded_at ? "Изменить ответ" : "Ответить"}
      </Button>
    </article>
  );
}

function FeedItem({ item }) {
  const label = formatNotificationLabel(item);
  const text = formatNotificationText(item);
  const toneClass =
    item.tone === "danger"
      ? s.notifFeedDanger
      : item.tone === "warn"
        ? s.notifFeedWarn
        : item.tone === "success"
          ? s.notifFeedSuccess
          : "";

  const body = (
    <article className={`${s.notifFeedItem} ${toneClass}`.trim()}>
      <div className={s.notifFeedHead}>
        <span className={s.notifTag}>{label}</span>
        <time className={s.notifFeedTime}>{formatWhen(item.sent_at)}</time>
      </div>
      <p className={s.notifFeedText}>{text}</p>
    </article>
  );

  if (item.url && !item.actionable) {
    const groupMatch = String(item.url).match(/\/app\/chat\/group\/([^/]+)$/);
    const linkState = groupMatch ? { name: item.title } : undefined;
    return (
      <Link to={item.url} state={linkState} className={s.notifFeedLink}>
        {body}
      </Link>
    );
  }
  return body;
}

export default function ClientNotifications() {
  const toast = useToast();
  const { athleteQueryParam } = useClientAthlete();
  const [feed, setFeed] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    let rows = [];
    let dashboard = null;
    try {
      const [notifRes, pollRes] = await Promise.allSettled([
        clinicAPI.getMyNotifications(athleteQueryParam),
        clinicAPI.getMyTrainingPolls(athleteQueryParam),
      ]);
      const logItems =
        notifRes.status === "fulfilled"
          ? notifRes.value.notifications || []
          : [];
      const pollItems =
        pollRes.status === "fulfilled"
          ? (pollRes.value.polls || [])
              .filter(pollNeedsResponse)
              .map(formatPollNotificationItem)
          : [];
      rows = mergeClientNotificationItems(logItems, pollItems);
    } catch (e) {
      console.error(e);
      toast.error(
        e.response?.data?.error || "Не удалось загрузить уведомления",
      );
    }
    try {
      dashboard = await clinicAPI.getMyClientDashboard(athleteQueryParam);
    } catch {
      dashboard = null;
    }
    setFeed(rows);
    setDash(dashboard);
    const init = {};
    for (const item of rows) {
      if (item.poll_row?.answers?.option_id) {
        const norm = normalizeSurveyRows([item.poll_row])[0] || item.poll_row;
        init[norm.id || item.poll_row.id] = item.poll_row.answers.option_id;
      }
    }
    setSelected(init);
    setLoading(false);
  }, [toast, athleteQueryParam]);

  useEffect(() => {
    load();
    clinicAPI
      .markMyNotificationsRead(athleteQueryParam)
      .then(() => {
        window.dispatchEvent(new Event("baysport:poll-activity"));
      })
      .catch(() => {});
  }, [load, athleteQueryParam]);

  const submit = async (row) => {
    const norm = normalizeSurveyRows([row])[0] || row;
    const rowKey = norm.id || row.id;
    const optionId = selected[rowKey] ?? selected[row.id];
    if (!optionId) {
      toast.error("Выберите вариант");
      return;
    }
    setBusyId(rowKey);
    try {
      const { routeId, body } = buildPollRespondPayload(norm, {
        option_id: optionId,
      });
      await clinicAPI.respondTrainingPoll(routeId, body, athleteQueryParam);
      toast.success("Ответ отправлен");
      notifyPollAnswered();
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  const alerts = useMemo(() => {
    const list = [];
    const card = dash?.profile?.patient_card;
    const membership = dash?.membership;
    const med = formatExpiryAlert(card?.medical_clearance_expires_at, "Допуск");
    if (med) list.push({ ...med, id: "med" });
    const memb = formatExpiryAlert(membership?.ends_at, "Абонемент");
    if (memb && membership?.status === "active") {
      list.push({
        ...memb,
        id: "memb",
        href: "/app/profile/account/club",
        action: "Продлить",
      });
    } else if (memb) {
      list.push({ ...memb, id: "memb" });
    }
    const pendingPay =
      membership?.status === "pending"
        ? membership
        : dash?.pending_membership?.status === "pending"
          ? dash.pending_membership
          : (dash?.memberships || []).find((m) => m?.status === "pending");
    if (pendingPay) {
      list.push({
        id: "pay",
        tone: "warn",
        text: `Ожидается оплата: ${pendingPay.plan?.name || "абонемент"}`,
        href: "/app/profile/account/club",
        action: "Оплатить",
      });
    }
    return list;
  }, [dash]);

  const pollItems = feed.filter((item) => item.actionable && item.poll_row);
  const feedItems = feed.filter((item) => !item.actionable || !item.poll_row);

  if (loading) {
    return (
      <div className={s.notifPage}>
        <header className={s.notifPageHead}>
          <Link to="/app" className={s.notifPageBack}>
            ← Назад
          </Link>
          <h1>Уведомления</h1>
        </header>
        <Skeleton height={200} style={{ borderRadius: 16, margin: "0 16px" }} />
      </div>
    );
  }

  return (
    <div className={s.notifPage}>
      <header className={s.notifPageHead}>
        <Link to="/app" className={s.notifPageBack}>
          ← Назад
        </Link>
        <h1>Уведомления</h1>
      </header>
      <div className={s.notif}>
        {alerts.length > 0 ? (
          <section className={s.notifSection}>
            <h2>Важное</h2>
            {alerts.map((a) =>
              a.href ? (
                <Link
                  key={a.id}
                  to={a.href}
                  className={`${s.notifAlert} ${
                    a.tone === "danger" ? s.notifAlertDanger : s.notifAlertWarn
                  } ${s.notifAlertLink}`}
                >
                  <span>{a.text}</span>
                  {a.action ? <span className={s.notifAlertAction}>{a.action} →</span> : null}
                </Link>
              ) : (
                <div
                  key={a.id}
                  className={`${s.notifAlert} ${
                    a.tone === "danger" ? s.notifAlertDanger : s.notifAlertWarn
                  }`}
                >
                  {a.text}
                </div>
              ),
            )}
          </section>
        ) : null}

        {pollItems.length > 0 ? (
          <section className={s.notifSection}>
            <h2>Требует ответа</h2>
            {pollItems.map((item) => {
              const norm =
                normalizeSurveyRows([item.poll_row])[0] || item.poll_row;
              const rowKey = norm.id || item.poll_row.id;
              return (
              <PollCard
                key={item.id}
                row={item.poll_row}
                selected={selected[rowKey]}
                busy={busyId === rowKey}
                onSelect={(id) =>
                  setSelected((p) => ({ ...p, [rowKey]: id }))
                }
                onSubmit={() => submit(item.poll_row)}
              />
            );
            })}
          </section>
        ) : null}

        <section className={s.notifSection}>
          <h2>Лента</h2>
          {feedItems.length === 0 ? (
            <p className={s.notifEmpty}>
              Пока пусто — здесь появятся новости клуба, напоминания о тренировках,
              опросы и другие уведомления
            </p>
          ) : (
            <div className={s.notifFeedList}>
              {feedItems.map((item) => (
                <FeedItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
